"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, type ReactNode } from "react";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { getRankHoverBorderClass, getRankStaticBorderClass } from "@/components/waves/drops/dropRankStyles";
import type { DropInteractionParams } from "@/components/waves/drops/drop.types";
import { DropLocation, hasDropFooter } from "@/components/waves/drops/drop.types";
import useIsMobileDevice from "@/hooks/isMobileDevice";
import WaveDropActions from "@/components/waves/drops/WaveDropActions";
import MemeWinnerHeader from "./MemeWinnerHeader";
import MemeWinnerDescription from "./MemeWinnerDescription";
import MemeWinnerArtistInfo from "./MemeWinnerArtistInfo";
import MemeDropTraits from "./MemeDropTraits";
import DropMobileMenuHandler from "@/components/waves/drops/DropMobileMenuHandler";
import DropListItemContentMedia from "@/components/drops/view/item/content/media/DropListItemContentMedia";
import { useDropContext } from "@/components/waves/drops/DropContext";
import { WaveWinnerIdentity } from "@/components/waves/winners/identity/WaveWinnerIdentity";
interface MemeWinnerDropProps {
  readonly drop: ExtendedDrop;
  readonly showReplyAndQuote: boolean;
  readonly onReply: (param: DropInteractionParams) => void;
  readonly footer?: ReactNode;
  readonly showInteractions?: boolean | undefined;
}
const getRankHoverClass = (rank: number | null): string => {
  return getRankHoverBorderClass(rank);
};
const getNonEmptyText = (value: string | null | undefined): string | undefined => {
  const trimmed = value?.trim();
  return trimmed && trimmed.length > 0 ? trimmed : undefined;
};
const getMetadataValue = (drop: ExtendedDrop, dataKey: string): string | undefined => getNonEmptyText(drop.metadata.find(metadata => metadata.data_key === dataKey)?.data_value);
export default function MemeWinnerDrop(t0) {
  const $ = _c(56);
  const {
    drop,
    showReplyAndQuote,
    onReply,
    footer,
    showInteractions: t1
  } = t0;
  const showInteractions = t1 === undefined ? true : t1;
  const isMobile = useIsMobileDevice();
  const {
    location
  } = useDropContext();
  let t2;
  if ($[0] !== drop) {
    t2 = getNonEmptyText(drop.title) ?? getMetadataValue(drop, "title") ?? "Artwork Title";
    $[0] = drop;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const title = t2;
  let t3;
  if ($[2] !== drop) {
    t3 = getNonEmptyText(drop.parts.at(0)?.content) ?? getMetadataValue(drop, "description") ?? "This is an artwork submission for The Memes collection.";
    $[2] = drop;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const description = t3;
  let t4;
  if ($[4] !== drop.parts) {
    t4 = drop.parts.at(0)?.media.at(0);
    $[4] = drop.parts;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const artworkMedia = t4;
  let t5;
  if ($[6] !== drop || $[7] !== onReply) {
    t5 = () => {
      onReply({
        drop,
        partId: drop.parts[0]?.part_id
      });
    };
    $[6] = drop;
    $[7] = onReply;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const handleOnReply = t5;
  const effectiveRank = drop.winning_context?.place ?? drop.rank;
  let t6;
  if ($[9] !== drop) {
    t6 = <div className="tw-border-x-0 tw-border-b tw-border-t-0 tw-border-solid tw-border-white/5 tw-bg-iron-900/30 tw-p-4 tw-pb-3"><MemeWinnerArtistInfo drop={drop} /></div>;
    $[9] = drop;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== title) {
    t7 = <MemeWinnerHeader title={title} />;
    $[11] = title;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== description) {
    t8 = <MemeWinnerDescription description={description} />;
    $[13] = description;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== t7 || $[16] !== t8) {
    t9 = <div className="tw-px-4 tw-pb-4 tw-pt-4"><div className="tw-space-y-1">{t7}{t8}</div></div>;
    $[15] = t7;
    $[16] = t8;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== drop) {
    t10 = <WaveWinnerIdentity drop={drop} variant="full" className="tw-px-4 tw-pb-4" />;
    $[18] = drop;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== artworkMedia) {
    t11 = artworkMedia && <div className="tw-mx-0.5 tw-flex tw-h-96 tw-justify-center tw-bg-iron-950"><DropListItemContentMedia media_mime_type={artworkMedia.mime_type} media_url={artworkMedia.url} isCompetitionDrop={true} /></div>;
    $[20] = artworkMedia;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== drop) {
    t12 = <div className="tw-mt-4 tw-hidden tw-border-x-0 tw-border-b-0 tw-border-t tw-border-solid tw-border-white/5 tw-bg-iron-900/30 tw-p-4 lg:tw-block"><MemeDropTraits drop={drop} /></div>;
    $[22] = drop;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== t10 || $[25] !== t11 || $[26] !== t12 || $[27] !== t6 || $[28] !== t9) {
    t13 = <>{t6}{t9}{t10}{t11}{t12}</>;
    $[24] = t10;
    $[25] = t11;
    $[26] = t12;
    $[27] = t6;
    $[28] = t9;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  const content = t13;
  const t14 = `tw-w-full ${location === DropLocation.WAVE ? "tw-px-4 tw-py-1" : ""} tw-group tw-relative`;
  let t15;
  if ($[30] !== effectiveRank) {
    t15 = getRankStaticBorderClass(effectiveRank);
    $[30] = effectiveRank;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] !== effectiveRank) {
    t16 = getRankHoverClass(effectiveRank);
    $[32] = effectiveRank;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  const t17 = `tw-overflow-hidden tw-rounded-xl tw-border tw-border-solid ${t15} tw-transition-all tw-duration-200 tw-ease-out ${location === DropLocation.WAVE ? "tw-bg-iron-900/80" : "tw-bg-iron-950"} ${t16}`;
  let t18;
  if ($[34] !== content || $[35] !== drop || $[36] !== handleOnReply || $[37] !== showInteractions || $[38] !== showReplyAndQuote) {
    t18 = showInteractions ? <DropMobileMenuHandler drop={drop} showReplyAndQuote={showReplyAndQuote} onReply={handleOnReply}>{content}</DropMobileMenuHandler> : content;
    $[34] = content;
    $[35] = drop;
    $[36] = handleOnReply;
    $[37] = showInteractions;
    $[38] = showReplyAndQuote;
    $[39] = t18;
  } else {
    t18 = $[39];
  }
  let t19;
  if ($[40] !== footer) {
    t19 = hasDropFooter(footer) && <div className="tw-px-4 tw-pb-4 tw-pt-2">{footer}</div>;
    $[40] = footer;
    $[41] = t19;
  } else {
    t19 = $[41];
  }
  let t20;
  if ($[42] !== drop || $[43] !== handleOnReply || $[44] !== isMobile || $[45] !== showInteractions || $[46] !== showReplyAndQuote) {
    t20 = !isMobile && showInteractions && showReplyAndQuote && <div className="tw-absolute tw-right-4 tw-top-2"><WaveDropActions drop={drop} activePartIndex={0} onReply={handleOnReply} /></div>;
    $[42] = drop;
    $[43] = handleOnReply;
    $[44] = isMobile;
    $[45] = showInteractions;
    $[46] = showReplyAndQuote;
    $[47] = t20;
  } else {
    t20 = $[47];
  }
  let t21;
  if ($[48] !== t17 || $[49] !== t18 || $[50] !== t19 || $[51] !== t20) {
    t21 = <div className={t17}>{t18}{t19}{t20}</div>;
    $[48] = t17;
    $[49] = t18;
    $[50] = t19;
    $[51] = t20;
    $[52] = t21;
  } else {
    t21 = $[52];
  }
  let t22;
  if ($[53] !== t14 || $[54] !== t21) {
    t22 = <div className="tw-mb-3 tw-w-full"><div className={t14}>{t21}</div></div>;
    $[53] = t14;
    $[54] = t21;
    $[55] = t22;
  } else {
    t22 = $[55];
  }
  return t22;
}